/**
 * The template map: which eighteen rectangles actually reach the avatar.
 *
 * The homepage's one genuinely novel thing. Nobody else publishes this, it
 * answers a question people really ask ("which parts of the template are
 * wasted?"), and it works with **no 3D at all** — which is why it is built
 * first. When the viewer lands it gains a second half: selecting a region will
 * light up the matching face on the body via `highlightRegion()`. Until then it
 * is complete on its own, and it stays the fallback for any device that cannot
 * run WebGL.
 *
 * SVG rather than canvas. Each face becomes a real element with a real
 * accessible name and real focus, hit-testing is free, and it scales to any
 * width without a redraw — none of which is true of a canvas without
 * reimplementing all three.
 *
 * See docs/decisions/homepage.md §9.
 */
import { CANVAS, type GarmentType, REGION_IDS, type RegionId } from "../../shared/format";
import { deadSpacePct, regionFacts } from "../../shared/region-facts";
import { REGIONS } from "../../shared/regions";
import { FACE, type FaceKey } from "../../shared/tokens";
import { drawTemplate } from "./template-file";

const SVG = "http://www.w3.org/2000/svg";

export interface RegionMapOptions {
  garment?: GarmentType;
  /** Told on every change, so the viewer can be wired in later without edits here. */
  onSelect?: (id: RegionId | null) => void;
}

export interface RegionMap {
  el: HTMLElement;
  select(id: RegionId | null): void;
  /**
   * Show the template underneath, and switch the faces from filled to outlined.
   *
   * The map has two jobs and does them one at a time: with nothing loaded it
   * teaches the layout, and once artwork is there it frames it instead of
   * competing with it — outlines only, so the outfit is what you see. Null puts
   * it back to teaching.
   */
  setArtwork(source: CanvasImageSource | null, size?: { width: number; height: number }): void;
  /**
   * Replace the line under the map when nothing is selected.
   *
   * The default is the 54.9% fact, which is the right thing to say about an
   * empty template and the wrong thing to say about somebody's artwork. Null
   * restores it.
   */
  setNote(text: string | null): void;
  /**
   * Shirt or pants. Relabels every face — the same rectangle is a *sleeve* on
   * one and a *leg* on the other, so labels that ignore the garment are wrong
   * half the time.
   */
  setGarment(next: "shirt" | "pants"): void;
  destroy(): void;
}

function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number> = {},
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(SVG, tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  return node;
}

export function createRegionMap(opts: RegionMapOptions = {}): RegionMap {
  let garment: GarmentType = opts.garment ?? "shirt";
  const root = document.createElement("div");
  root.className = "rmap";

  const svg = svgEl("svg", {
    viewBox: `0 0 ${CANVAS.width} ${CANVAS.height}`,
    class: "rmap__svg",
    role: "group",
    "aria-label": "Template faces",
  });

  /*
   * Dead space is drawn as a full-canvas fill with the live faces painted over
   * it, rather than as a punched-out path. The visual result is identical and
   * it needs no even-odd geometry — the faces are the authority, so nothing can
   * drift out of step with them.
   */
  /*
   * `appendChild`, not `append`, throughout this file. `tsconfig` pulls in
   * @cloudflare/workers-types for the Worker, whose HTMLRewriter `Element`
   * shadows the DOM one — its `append` takes a string or a Response, so passing
   * a node is a type error. Same trap `main.ts` documents at the garment
   * <select>.
   */
  svg.appendChild(
    svgEl("rect", { x: 0, y: 0, width: CANVAS.width, height: CANVAS.height, class: "rmap__dead" }),
  );

  const shapes = new Map<RegionId, SVGRectElement>();
  for (const id of REGION_IDS) {
    const r = REGIONS[id];
    /*
     * Coloured with Roblox's own face palette, not our accent.
     *
     * Two reasons. It is the colour scheme printed on the official template, so
     * anyone who has opened one recognises this immediately. And the viewer
     * tints its highlight with the same `FACE[side]` token — its comment says
     * that is "so it matches the 2D gutter colour exactly rather than being an
     * approximation of it" — which only pays off if the 2D side actually uses
     * it. Picking a face here and lighting it there are now literally the same
     * colour.
     */
    const face = id.split(".")[1] as FaceKey;
    const rect = svgEl("rect", {
      x: r.x,
      y: r.y,
      width: r.w,
      height: r.h,
      fill: FACE[face] ?? "#888888",
      // Also as `color`, so the outline mode can glow in the face's own hue
      // via currentColor without repeating the palette in CSS.
      color: FACE[face] ?? "#888888",
      class: "rmap__face",
      role: "button",
      "aria-label": regionFacts(id, garment).label,
      "aria-pressed": "false",
      /*
       * Roving tabindex: exactly one face is in the tab order and the arrow
       * keys move between them. Eighteen tab stops in the middle of a marketing
       * page is a wall somebody has to press Tab through to get past, which is
       * why the toolbar pattern exists.
       */
      tabindex: -1,
      "data-region": id,
      "data-group": regionFacts(id, garment).group,
    });
    shapes.set(id, rect);
    svg.appendChild(rect);

    /*
     * The letter Roblox prints on its own template.
     *
     * F / B / L / R / U / D is the notation on the official artwork, so anyone
     * who has opened one reads this without a key — the same reasoning as
     * taking the palette from `FACE`. Names in full would not fit: the
     * narrowest face is 32px wide in a 585px viewBox.
     *
     * `pointer-events: none` in CSS, so the letter never swallows a click
     * meant for the face under it.
     */
    const tag = svgEl("text", {
      x: r.x + r.w / 2,
      y: r.y + r.h / 2,
      class: "rmap__tag",
      "aria-hidden": "true",
      "data-region": id,
    });
    tag.textContent = (face[0] ?? "").toUpperCase();
    svg.appendChild(tag);
  }

  const readout = document.createElement("p");
  readout.className = "rmap__readout";
  readout.setAttribute("aria-live", "polite");

  let selected: RegionId | null = null;
  /** Overrides the idle readout once there is artwork to say something about. */
  let note: string | null = null;

  function render() {
    // Labels are garment-dependent, so they are re-read here rather than baked
    // in at construction.
    for (const [id, rect] of shapes) {
      rect.setAttribute("aria-label", regionFacts(id, garment).label);
    }
    for (const [id, rect] of shapes) {
      rect.classList.toggle("is-on", id === selected);
      // Dim everything else only once something is chosen, so the default
      // state is the honest picture rather than a puzzle.
      rect.classList.toggle("is-off", selected !== null && id !== selected);
    }
    for (const [id, rect] of shapes) {
      rect.setAttribute("aria-pressed", String(id === selected));
      // The tab stop follows the selection, so returning to the map lands on
      // the face you were last looking at rather than back at the start.
      rect.setAttribute("tabindex", id === (selected ?? REGION_IDS[0]) ? "0" : "-1");
    }
    if (!selected) {
      readout.textContent =
        note ?? `${deadSpacePct()}% of this canvas never appears on the avatar.`;
      return;
    }
    readout.textContent = regionFacts(selected, garment).label;
  }

  function select(id: RegionId | null) {
    // Tapping the chosen one again clears it. On a phone there is no other way
    // back to the overview, because there is no "move the pointer away".
    selected = id === selected ? null : id;
    render();
    opts.onSelect?.(selected);
  }

  const regionOf = (e: Event) =>
    ((e.target as Element)?.closest("[data-region]")?.getAttribute("data-region") ??
      null) as RegionId | null;

  const onSvgClick = (e: Event) => {
    const id = regionOf(e);
    if (id) select(id);
  };
  svg.addEventListener("click", onSvgClick);

  /*
   * Arrow keys walk the faces in the order they are drawn, which is the order
   * the template lays them out — torso first, then each limb. Enter and Space
   * select, because a role="button" must answer to both.
   */
  const onKey = (e: KeyboardEvent) => {
    const id = regionOf(e);
    if (!id) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      select(id);
      return;
    }
    const step =
      e.key === "ArrowRight" || e.key === "ArrowDown"
        ? 1
        : e.key === "ArrowLeft" || e.key === "ArrowUp"
          ? -1
          : 0;
    if (!step) return;
    e.preventDefault();
    const i = REGION_IDS.indexOf(id);
    const next = REGION_IDS[(i + step + REGION_IDS.length) % REGION_IDS.length];
    if (!next) return;
    const rect = shapes.get(next);
    rect?.setAttribute("tabindex", "0");
    (rect as unknown as HTMLElement | undefined)?.focus();
    select(next);
  };
  svg.addEventListener("keydown", onKey as EventListener);

  // Pointer-only preview. Selection is the state; on touch this never fires,
  // which is exactly why it is not the mechanism.
  const onOver = (e: Event) => {
    if (selected) return;
    const id = regionOf(e);
    if (id) shapes.get(id)?.classList.add("is-hover");
  };
  const onOut = (e: Event) => {
    const id = regionOf(e);
    if (id) shapes.get(id)?.classList.remove("is-hover");
  };
  svg.addEventListener("pointerover", onOver);
  svg.addEventListener("pointerout", onOut);

  const figure = document.createElement("div");
  figure.className = "rmap__stage";
  /*
   * The artwork sits behind the SVG, in its own canvas rather than sharing the
   * viewer's — one canvas cannot be painted in two places, and copying 1.3 MB
   * once per upload is cheaper than any arrangement that could.
   */
  const art = document.createElement("canvas");
  art.className = "rmap__art";
  art.width = CANVAS.width;
  art.height = CANVAS.height;
  art.hidden = true;
  figure.appendChild(art);
  figure.appendChild(svg);

  root.appendChild(figure);
  root.appendChild(readout);
  render();

  return {
    el: root,
    select,
    setArtwork(source, size) {
      const ctx = art.getContext("2d");
      ctx?.clearRect(0, 0, art.width, art.height);
      art.hidden = !source;
      // Same class drives the fills-to-outlines switch, so the picture and the
      // styling can never disagree about whether there is artwork.
      root.classList.toggle("has-art", Boolean(source));
      if (source && ctx) {
        drawTemplate(art, source, size ?? { width: art.width, height: art.height });
      }
    },

    setNote(text) {
      note = text;
      render();
    },

    setGarment(next) {
      if (next === garment) return;
      garment = next;
      render();
    },
    destroy() {
      svg.removeEventListener("click", onSvgClick);
      svg.removeEventListener("keydown", onKey as EventListener);
      svg.removeEventListener("pointerover", onOver);
      svg.removeEventListener("pointerout", onOut);
      root.remove();
    },
  };
}
